import React, { useEffect, useState } from 'react'
import LavorisService from '../../services/LavorisService';
import { useModal } from '../../hooks';
import { useAlert } from '../../hooks/useAlert';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Badge, BadgeProps } from '@mui/material';
import moment from 'moment';
import { filterMoldeExamPractice } from '../../utils/filterMoldeExamPractice';
import { RequestButton } from '../../components/others/RequestTurnButton';
import { employeeAbsences } from '../Absences/Types/Absences.type';
import AbsenteeismResult from '../others/absenteeismResult';
import { TableActions } from '../../components/others/TableActions';
import { handleOpenPdfReport, handleOpenPdfReportPractice } from '../../utils/files';
import { extractExamFromMetadata, extractPracticesFromMetadata } from '../../utils/metadata';
import MultiplePracticesModal from './MultiplePracticesModal';
import { DetailTurnWithDate } from '../others/detailsTurn';
import exelReports from './exelReports';
import InfiniteTable from "../../components/others/InfiniteTableComponent/IntiniteTableComponent";
import InfoOffcanvas from '../../components/others/InfoOffCanvas/InfoOffCanvas';
import "./Reports.scss";
import { formatDateTimeForTable } from '../../utils/formatDate';

const VIEW_APPOINTMENT_MODAL = "viewAppointmentModal";
const VIEW_MULTIPLE_PRACTICES_MODAL = 'viewMultiplePracticesModal'

const SearchByDni = () => {
    const controller = new AbortController();
    const signal = controller.signal;
    const LavorisInstance = LavorisService.getInstance();
    const { closeModal, openModal, isActive } = useModal();
    const { showAlert } = useAlert();
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const hasSearchQuery = searchQuery && searchQuery.length > 0
    const [hasMore, setHasMore] = useState(true);
    const [data, setData] = useState<any[]>([]);
    const [loader, setLoader] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<employeeAbsences>();
    const [rowData, setRowData] = useState<any>()
    const [appId, setAppId] = useState<string>("")

    const onSearch = async () => {
        setLoader(true);
        try {
            const app = await LavorisInstance.TurnServices.getAppointmentsByDNI(searchQuery || "", signal)

            // Filtrar registros con status "Aptitud parcialmente completada"
            const filteredApp = app.filter((item: any) => item.status !== 'Aptitud parcialmente completada');

            if (filteredApp.length === 0) {
                showAlert("No se encontraron registros");
                setHasMore(false);
                return;
            }
            setData(filteredApp)

        } catch (err: any) {
            console.log(err);
            setHasMore(false);
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        return () => {
            controller.abort()
        };
    }, [])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const querySearch = e.target.value;
        setSearchQuery(querySearch);
    }

    const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#f5f5f9',
            color: 'rgba(0, 0, 0, 0.87)',
            maxWidth: 220,
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #dadde9',
        },
    }));

    const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
        '& .MuiBadge-badge': {
            right: -3,
            top: 13,
            border: `2px solid ${theme.palette.background.paper}`,
            padding: '0 4px',
            margin: '0  4px'
        },
    }));

    const returnDateTime = (rowData: any) => {
        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') return "---";
        if (rowData?.startTime) return formatDateTimeForTable(rowData?.date, rowData?.startTime);
        return "---";
    }

    const returnAdmissionDate = (rowData: any) => {
        if (rowData?.report?.visitManualTime) return rowData?.report?.visitManualTime;
        if (rowData?.is_prestador && rowData?.status === 'Finalizado') return rowData?.admissionTime;
        if (!rowData?.actions || rowData?.actions?.length === 0) return rowData?.admissionTime || "---";
        if (!rowData?.admissionTime) return '---';
        const actions = ['ADMITIDO', 'ADMITIDO/TOTEM']
        const admitido = rowData?.actions.filter((action: any) => actions.includes(action.action));
        if (!rowData?.is_prestador && admitido.length === 0) return rowData?.admissionTime || "---";
        return moment(admitido[0].date).format('DD/MM/YY HH:mm');
    }

    const returnCreation = (rowData: any) => {
        if (!rowData?.actions || rowData?.actions?.length === 0) return moment(rowData.createdDate).format('DD/MM/YY HH:mm') || '---';
        const actions = ['SOLICITADO', 'CREADO', 'CREACION/REPROGRAMACION']
        const action = rowData?.actions.filter((action: any) => actions.includes(action.action));
        if (action.length === 0) return moment(rowData.createdDate).format('DD/MM/YY HH:mm') || '---';
        return moment(action[0].date).format('DD/MM/YY HH:mm');
    }

    const returnName = (rowData: any) => {
        let name = rowData?.metadata?.patient?.name?.toUpperCase() || rowData?.patient?.name?.toUpperCase() || rowData?.patient?.employee?.user?.name?.toUpperCase() || rowData?.employee?.name?.toUpperCase() || "---"
        return name
    }

    const returnDescription = (rowData: any) => {
        if (rowData.type === 'Videollamada') return 'Telecontrol'
        if (rowData.type === 'Visita a Domicilio') return 'Control en Domicilio'
        if (rowData.type === 'Consulta en Lavoris') return 'Control en Consultorio Lavoris'
        if (rowData.type === 'Define Lavoris') return 'AI - Ausentismo Inteligente'
        return filterMoldeExamPractice(rowData)
    }

    const returnResults = (rowData: any) => {
        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') {
            const report = rowData?.report
            const diagnostico = report?.diagnostic
            return diagnostico || "---";
        }

        const hasOnlyIsolatedPractices = (row: any) => {
            if (row.metadata === undefined) return false;
            const practices = extractPracticesFromMetadata(row.metadata);
            if (!practices) return false;
            return practices.every((p: any) => p.type === 'PRACTICE' || p.type === 'PRACTICE_IN_MOLDE');
        };

        const findExam = (appointment: any) => {
            if (!appointment?.metadata) return;
            return extractExamFromMetadata(appointment?.metadata);
        };

        const findConclusion = (exam: any): string => {
            if (!exam) return "";
            const conclusion =
                exam?.form?.find((field: any) => field.type === "conclusiones" || field.type === "select")?.response
                    ?.input_value || "";
            return conclusion.replaceAll("_", " ").toUpperCase();
        };

        const findConclusionComents = (exam: any): string => {
            if (!exam) return "";
            const conclusionComments =
                exam?.form?.find((field: any) => field.type === "textarea")?.response
                    ?.input_value || null;
            return conclusionComments || "";
        }

        const insolatePracticeResult = (rowData: any) => {
            const practices = extractPracticesFromMetadata(rowData.metadata)
            const practicesForm = practices?.map((p: any) => p?.form)
            const result = practicesForm?.map((form: any) => {
                const controlEnConsultorio = form?.find((element: any) => element?.label === 'Control en Consultorio');
                if (controlEnConsultorio) {
                    const diagnostico = form?.find((element: any) => element?.label === 'Diagnósticos');
                    if (diagnostico) {
                        return diagnostico?.response?.input_value?.label;
                    }
                }

                const conclusion = form?.filter((element: any) => element?.label === 'Conclusión' && element?.response)[0];
                if (conclusion) return conclusion?.response?.input_value;

                const icTraumatologica = form?.find((element: any) => element?.label === 'Interconsulta Traumatológica');
                if (icTraumatologica) {
                    const informe = form?.filter((element: any) => element?.label === 'Informe' && element?.response)[0];
                    return informe?.response?.input_value;
                }

                const observation = form?.filter((element: any) => element?.label === 'Observacion' && element?.response)[0];
                if (observation) return observation?.response?.input_value + ",";
                return null;
            });
            return result?.filter((r: any) => r !== null).join(", ") || "---";
        }

        if (hasOnlyIsolatedPractices(rowData)) {
            return insolatePracticeResult(rowData);
        }

        const exam = findExam(rowData);
        if (!exam) return "---";

        const conclusion = findConclusion(exam)
            .split("_")
            .join(" ")
            .toLocaleLowerCase();

        const conclusionComments = findConclusionComents(exam);
        const fullConclusion = conclusionComments ? `${conclusion}: ${conclusionComments}` : conclusion;
        return fullConclusion || "---";
    }

    const giaSolicitud = (rowData: any) => {
        if (!rowData) return '---'
        const gia = rowData?.gia ? 'Si' : 'No'
        return (
            <p className={`${rowData.gia ? 'text-green-700' : 'text-red-700'} font-extrabold`}>{gia} </p>
        )
    }

    const get_constrol_consultorio_parte_medico = async (id: string) => {
        try {
            const getUrlId = await LavorisInstance.TurnServices.getAppPdfFile(id);
            if (getUrlId.length === 0) {
                showAlert('No hay PDF creado, comuniquese con Lavoris');
                return;
            }
            await LavorisInstance.FilesService.getReportAWSFileById(getUrlId);

        } catch (err) {
            console.log(err);
        }
    }

    const actions = (rowData: any) => {
        const hasOnlyIsolatedPractices = (row: any) => {
            if (row.status !== 'Finalizado') return false;
            if (rowData.metadata === undefined) return false;
            const practices = extractPracticesFromMetadata(row.metadata);
            if (!practices) return false;

            return practices.every((p: any) => p.type === "PRACTICE" || p.type === 'PRACTICE_IN_MOLDE');
        };
        const { molde, exam, practices } = rowData?.metadata || {};
        const isControlConsultorioMolde = molde?.name?.includes('Control consultorio');
        const isControlConsultorioPractice =
            !isControlConsultorioMolde &&
            !exam &&
            practices?.length === 1 &&
            (practices[0]?.managerId === '1026' || practices[0]?.managerId === '2023');

        const isControlConsultorio = isControlConsultorioMolde || isControlConsultorioPractice;

        const downloadSequentiallyPDFs = async (metadata: any) => {
            const practices = extractPracticesFromMetadata(metadata);
            const patient = metadata.patient.name
            if (!practices) return false;

            for (const p of practices) {
                await handleOpenPdfReportPractice((p as any).pdfUrl, p.name, patient);
            }
        };

        const findExam = (appointment: any) => {
            if (!appointment?.metadata) return;
            return extractExamFromMetadata(appointment?.metadata);
        };

        const hasExamFullReport = (row: any) => !!findExam(row)?.fullReportUrl;

        const getExamPDF = (metadata: any) => {
            const exam = extractExamFromMetadata(metadata);
            if (!exam?.fullReportUrl) throw new Error("No PDF generated for exam");

            return handleOpenPdfReport(exam.fullReportUrl);
        };



        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') return (<div className="flex flex-wrap justify-center align-middle gap-2">
            <RequestButton
                text={"Ver detalles"}
                type={"button"}
                onClick={() => {
                    setSelectedEmployee(rowData);
                    openModal("modalReporteAusentismo", rowData.id);
                }}
            />
            {rowData.status === "Completada" && (
                <>
                    <TableActions
                        type="Whatsapp"
                        title={!rowData.employee_ws_sent ? 'Parte médico no enviado' : 'Parte médico enviado'}
                        color={!rowData.employee_ws_sent ? 'red' : 'green'}
                        onClick={() => { }}
                    />

                    <TableActions
                        type="Download"
                        title="Descargar parte médico Empleado"
                        onClick={async () => {
                            await LavorisInstance.FilesService.getReportAWSFileById(
                                rowData?.reportFileIdEmployee,
                            );
                        }}
                    />
                </>
            )}
        </div>)

        if (rowData.status === 'Aptitud parcialmente completada') return 'Informe Pendiente';

        return (
            <div className="flex flex-wrap items-center justify-center gap-2">
                <TableActions
                    type="View"
                    onClick={() =>
                        openModal(VIEW_APPOINTMENT_MODAL, rowData.id)
                    }
                />
                {rowData?.status === 'Ausente' || rowData?.status === 'Eliminado' || rowData?.status === 'Cancelado' ? (
                    <TableActions
                        type="Time"
                        title={rowData?.status === 'Ausente' ? 'No se presentó' : '---'}
                        color={'blue'}
                        onClick={() => { }}
                    />
                ) : (
                    <TableActions
                        type="Time"
                        title={rowData?.tardiness || ''}
                        color={rowData?.is_late ? 'red' : 'green'}
                        onClick={() => { }}
                    />
                )}
                {hasExamFullReport(rowData) && (
                    <TableActions
                        type="PDF"
                        title={'Descargar Informe'}
                        onClick={() => getExamPDF(rowData.metadata)}
                    />
                )}
                {hasOnlyIsolatedPractices(rowData) && (
                    <>
                        <TableActions
                            type="Multiple PDF"
                            title="Ver PDFs indivualmente"
                            onClick={() => {
                                setRowData(rowData)
                                setAppId(rowData.id)
                                openModal(VIEW_MULTIPLE_PRACTICES_MODAL)
                            }}
                        />
                        <TableActions
                            type="Download"
                            title="Descargar PDF múltiples"
                            onClick={() => downloadSequentiallyPDFs(rowData.metadata)}
                        />
                    </>
                )}
            </div>
        );


    }

    data.sort((a, b) => {
        const createdDateA = new Date(a.createdDate);
        const createdDateB = new Date(b.createdDate);
        // Ordenar en orden descendente (de lo más reciente a lo más antiguo)
        return createdDateB.getTime() - createdDateA.getTime();
    });

    return (
        <div className="reports-container">
            {/* <div className="flex justify-center align-middle gap-2 bg-slate-200 p-4 mb-1 rounded-md">
                <div className=" flex justify-center align-middle gap-2">
                    <Button variant="contained" onClick={() => {
                        setData([])
                        setSearchQuery("")
                    }}>Limpiar</Button>
                    <input
                        title={'Ingresar dni o pasaporte'}
                        type='search'
                        placeholder='Ingresar dni o pasaporte'
                        className="rounded-lg border-gray-600 h-10"
                        onChange={handleSearchChange}
                    />

                    <button
                        className={`
                            block rounded-lg 
                            text-white font-bold  
                            w-40 ${hasSearchQuery ? "bg-red-500 hover:bg-red-700" : "bg-red-400 cursor-not-allowed"}`
                        }
                        title={hasSearchQuery ? "" : "Debe ingresar un DNI o pasaporte válido"}
                        disabled={hasSearchQuery ? false : true}
                        onClick={onSearch}>
                        Buscar
                    </button>
                    <div>
                        <StyledBadge badgeContent={data.length === 0 ? null : data.length} color="primary">
                            <DescriptionIcon color="action" />
                        </StyledBadge>
                    </div>
                    <HtmlTooltip
                        title={
                            <React.Fragment>
                                <Typography color="inherit">INFORMACIÓN DE BÚSQUEDA</Typography>
                                <ul className="text-md p-auto m-auto">
                                    <li>- Se puede búscar por el DNI o Pasaporte.</li>
                                    <li>- El ordenamiento se basa en la fecha de SOLICITUD del servicio o prestación médica.</li>
                                    <li>- Trae todos los resultados del empleado o postulante.</li>
                                    <li>- Contempla todos los estados del turno.</li>
                                </ul>
                            </React.Fragment>
                        }
                    >
                        <Button> <PrivacyTipIcon sx={{ color: 'green' }} /></Button>
                    </HtmlTooltip>
                </div>

                {
                    loader && (
                        <div className="flex justify-end align-middle w-10 h-10">
                            <CircularProgress color="secondary" />
                        </div>
                    )
                }

                <div className='flex justify-self-end'>
                    <button
                        onClick={() => exelReports(data, true)}
                        title={'Descargar Reporte (hacer clic y esperar)'}
                    >
                        <img
                            src="/images/iconExcel.svg"
                            alt="ExcelDownloadButton"
                        />
                    </button>
                </div>
            </div> */}



            <div className="custom-header">
                <div className="header-controls">
                    {/* Search Section */}
                    <div className="search-section">
                        <div className="search-input-group">
                            <input
                                title="Ingresar dni, apellido o nombre"
                                type="search"
                                placeholder="Ingresa un DNI o Pasaporte"
                                className="search-input"
                                onChange={handleSearchChange}
                            />
                            <button
                                type="button"
                                className="search-btn"
                                onClick={() => {
                                    onSearch()
                                }}
                                title="Buscar"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="search-btn"
                                disabled={!hasSearchQuery}
                                onClick={() => {
                                    setData([])
                                    setSearchQuery("")
                                }}
                                title="Limpiar Filtros"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M3 21h18M8.5 16.5l7-7M8.5 16.5l-2.5-2.5 7-7 2.5 2.5M16.5 9.5l2 2"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <rect
                                        x="13"
                                        y="3"
                                        width="4"
                                        height="4"
                                        rx="1"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="actions-section">

                        {/* Download Button */}
                        <button
                            className="download-btn"
                            onClick={loader ? undefined : () => exelReports(data, true)}
                            title="Descargar Reporte (hacer clic y esperar)"
                            disabled={loader}
                        >
                            {loader ? (
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M8 10.667L8 2M8 10.667l-2.667-2.666M8 10.667l2.667-2.666"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                            <span className="btn-text">Excel</span>
                        </button>

                        <InfoOffcanvas title="INFORMACIÓN DE BÚSQUEDA">
                            <ul className="text-md p-auto m-auto">
                                <li>- Se puede búscar por el DNI o Pasaporte.</li>
                                <li>- El ordenamiento se basa en la fecha de SOLICITUD del servicio o prestación médica.</li>
                                <li>- Trae todos los resultados del empleado o postulante.</li>
                                <li>- Contempla todos los estados del turno.</li>
                            </ul>
                        </InfoOffcanvas>

                    </div>
                </div>
            </div>


            <InfiniteTable
                data={data}
                columns={[
                    {
                        key: 'createdDate',
                        title: 'Fecha de solicitud',
                        className: 'col-reports-created-date',
                        render: (value: any, row: any) => returnCreation(row)
                    },
                    {
                        key: 'dateTime',
                        title: 'Fecha y hora del turno',
                        className: 'col-reports-date-time',
                        render: (value: any, rowData: any) => returnDateTime(rowData)
                    },
                    {
                        key: 'admissionDate',
                        title: 'Fecha y hora de admisión / visita',
                        className: 'col-reports-admission-date',
                        render: (value: any, rowData: any) => returnAdmissionDate(rowData)
                    },
                    {
                        key: 'patientName',
                        title: 'Apellido y Nombre',
                        className: 'col-reports-patient-name',
                        render: (value: any, row: any) => returnName(row)
                    },
                    {
                        key: 'description',
                        title: 'Servicios y prestaciones',
                        className: 'col-reports-description',
                        render: (value: any, rowData: any) => returnDescription(rowData)
                    },
                    {
                        key: 'results',
                        title: 'Resultado',
                        className: 'col-reports-results',
                        render: (value: any, rowData: any) => returnResults(rowData)
                    },
                    {
                        key: 'actions',
                        title: 'Acciones',
                        className: 'col-reports-actions',
                        render: (value: any, rowData: any) => actions(rowData)
                    }
                ]}
                loading={loader}
                hasMore={hasMore}
                onLoadMore={() => { }}
                height={570}
                emptyMessage="Momentáneamente no hay datos para mostrar"
                loadingMessage={data.length === 0 ? "Cargando datos..." : "Buscando más datos..."}
                striped
                hover
                bordered
            />

            {/* <div>
                <div className="flex justify-between p-2 border-b bg-slate-300">
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Apellido y Nombre</div>
                    <div className="w-1/5 flex justify-center align-middle text-start font-bold">GIA</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Tipo</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Estado</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha de solicitud</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha y hora del turno</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha y hora de admisión / visita</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Descripción</div>
                    <div className="w-1/5 flex justify-center align-middle text-center font-bold">Acciones</div>
                </div>
                <InfiniteScroll
                    className="border"
                    dataLength={data.length}
                    next={() => { }}
                    hasMore={hasMore}
                    loader={null}
                    height={570}
                >
                    {data.length === 0 && (
                        <div className="flex justify-center align-middle my-5 text-lg text-red-600">
                            Momentaneamente no hay registros cargados para mostrar.
                        </div>
                    )}

                    {data?.map((rowData: any, i: number) => {
                        return (
                            <div key={i} className="flex justify-between p-2 border-b">
                                <div className="w-1/5 flex justify-start text-sm">
                                    {rowData?.metadata?.patient?.name?.toUpperCase() || rowData?.patient?.name?.toUpperCase() || rowData?.patient?.employee?.user?.name?.toUpperCase() || rowData?.employee?.name?.toUpperCase() || "---"}
                                </div>
                                <div className="w-1/5 flex justify-center align-middle text-center text-sm">
                                    {giaSolicitud(rowData) || "No disponible"}
                                </div>
                                <div className="w-1/5 flex justify-center text-sm">
                                    {returnAppType(rowData)}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {rowData.status}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {moment(rowData.createdDate).format('DD/MM/YYYY HH:mm') || "---"}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {returnDateTime(rowData)}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {returnAdmissionDate(rowData)}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {returnDescription(rowData)}
                                </div>
                                <div className="w-1/5 flex justify-center text-center text-sm">
                                    {actions(rowData)}
                                </div>
                            </div>
                        )
                    })}
                </InfiniteScroll>
            </div> */}





            {isActive("modalReporteAusentismo") && (
                <AbsenteeismResult
                    toggleModal
                    urlTittle={"modalReporteAusentismo"}
                    patient={selectedEmployee}
                    onClose={() => closeModal("modalReporteAusentismo")}
                />
            )}

            {isActive(VIEW_MULTIPLE_PRACTICES_MODAL) && (
                <MultiplePracticesModal
                    rowData={rowData}
                    appId={appId}
                    show
                    refreshTable={() => { }}
                    onClose={() => closeModal(VIEW_MULTIPLE_PRACTICES_MODAL)}
                    modalTitle={"Descargar prácticas"}
                    urlTittle={VIEW_MULTIPLE_PRACTICES_MODAL}
                    download={handleOpenPdfReport}
                />
            )}

            {isActive(VIEW_APPOINTMENT_MODAL) && (
                <DetailTurnWithDate
                    show
                    modalTitle={"Detalle de turno"}
                    urlTittle={VIEW_APPOINTMENT_MODAL}
                    closeModal={() => closeModal(VIEW_APPOINTMENT_MODAL)}
                />
            )}




        </div>
    )
}

export default SearchByDni